import { AlertTriangle, Check, Calculator } from 'lucide-react';

const STAGE_COLOURS = {
  1: 'text-sky-300',
  2: 'text-emerald-300',
  3: 'text-amber-300',
  4: 'text-fuchsia-300',
};

/**
 * Frank's Live Scoring Panel — recalculates as questions are entered, the way a spreadsheet
 * would. Its point is catching a mistyped weight before a member is ever scored on it.
 */
export const LiveScoringPanel = ({ preview, dimensionNames }) => {
  if (!preview) return null;

  const { question_count: count, question_count_required: required } = preview;

  return (
    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 lg:sticky lg:top-4"
         data-testid="live-scoring-panel">
      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
        <Calculator className="w-4 h-4 text-cyan-400" />
        Live Scoring
      </h4>

      <div className={`mt-3 text-sm rounded-md px-3 py-2 flex items-start gap-2 ${
        preview.ready_to_publish
          ? 'bg-emerald-500/10 text-emerald-300'
          : 'bg-amber-500/10 text-amber-300'
      }`} data-testid="live-scoring-status">
        {preview.ready_to_publish
          ? <><Check className="w-4 h-4 mt-0.5 shrink-0" /> Ready to publish</>
          : <>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {preview.problems.map((p, i) => <span key={i} className="block">{p}</span>)}
              </span>
            </>}
      </div>

      <p className="mt-3 text-xs text-gray-500">Questions</p>
      <p className="text-2xl font-semibold text-white" data-testid="live-scoring-question-count">
        {count}<span className="text-base text-gray-500"> / {required}</span>
      </p>

      <div className="mt-4">
        <p className="text-xs text-gray-500 mb-1.5">Dimensions in use</p>
        <div className="space-y-1">
          {preview.dimensions.map((d) => (
            <div key={d.dimension} className="text-xs flex items-center gap-2"
                 data-testid={`live-scoring-dimension-${d.dimension}`}>
              <span className="flex-1 truncate text-gray-300">
                {dimensionNames[d.dimension] || d.dimension}
              </span>
              <span className={d.meets_minimum_questions ? 'text-gray-400' : 'text-amber-300'}>
                {d.question_count}q
              </span>
              <span className="text-gray-500 w-20 text-right">
                {d.raw_min}–{d.raw_max} raw
              </span>
              <span className="text-cyan-300 w-14 text-right">{d.effective_weight}%</span>
            </div>
          ))}
          {preview.dimensions_absent.length > 0 && (
            <p className="text-xs text-gray-500 pt-1.5 leading-relaxed"
               data-testid="live-scoring-absent">
              Not measured here:{' '}
              {preview.dimensions_absent.map((k) => dimensionNames[k] || k).join(', ')}.
              Their weight is excluded and the rest are scaled to 100%.
            </p>
          )}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs text-gray-500 mb-1.5">
          Overall raw range {preview.overall_raw_min}–{preview.overall_raw_max}
        </p>
      </div>

      {preview.score_preview.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-1.5">What a participant would score</p>
          <div className="space-y-1">
            {preview.score_preview.map((r) => (
              <div key={r.rung} className="text-xs flex items-center gap-2"
                   data-testid={`live-scoring-rung-${r.rung}`}>
                <span className="flex-1 text-gray-400 truncate">{r.label}</span>
                <span className="text-white w-8 text-right">{r.score}</span>
                <span className={`w-24 text-right ${STAGE_COLOURS[r.stage]}`}>{r.stage_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 border-t border-gray-700 pt-3">
        <p className="text-xs text-gray-500 mb-1.5">Stage bands</p>
        <div className="space-y-1">
          {preview.stage_bands.map((b) => (
            <div key={b.stage} className="text-xs flex items-center gap-2">
              <span className={`flex-1 ${STAGE_COLOURS[b.stage]}`}>{b.name}</span>
              <span className="text-gray-400">{b.from}–{b.to}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-600 mt-2">
          From the current thresholds and dimension weights in Assessment Scoring.
        </p>
      </div>
    </div>
  );
};

export default LiveScoringPanel;
